import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getGraphQLClient } from '@/common/graphqlClient';
import { createMutationHook, createQueryHook } from '@/common/hooks/typedHookFactory';
import { QueryKeys } from '@/common/queryKeys';
import { graphql } from '@/generated/graphql';

const INSERT_DRIVER_DISTRICT_OLD = graphql(`
  mutation InsertDriverDistrictOld($driverId: String!, $districtId: String!) {
    insert_driver_district_one(object: { districtId: $districtId, driverId: $driverId }) {
      id
    }
  }
`);

const DELETE_DRIVER_DISTRICT_OLD = graphql(`
  mutation DeleteDriverDistrictOld($districtId: String!, $driverId: String!) {
    delete_driver_district(
      where: { driverId: { _eq: $driverId }, districtId: { _eq: $districtId } }
    ) {
      affected_rows
    }
  }
`);

export const useInsertDriverDistrictOld = createMutationHook(INSERT_DRIVER_DISTRICT_OLD, {
  errorMessage: 'Error adding driver district',
});

export const useDeleteDriverDistrictOld = createMutationHook(DELETE_DRIVER_DISTRICT_OLD, {
  errorMessage: 'Error removing driver district',
});

const GET_DRIVER_DISTRICT_LIST_BY_ID = graphql(`
  query getDriversDistrictListById($driverId: String!) {
    user_by_pk(id: $driverId) {
      id
      name
      driver_districts {
        id
        district {
          id
          name
        }
      }
    }
  }
`);

export const useGetDriversDistrictListById = createQueryHook(
  GET_DRIVER_DISTRICT_LIST_BY_ID,
  (variables) => [QueryKeys.GET_DRIVERS_DISTRICT_LIST_BY_ID, variables.driverId],
  {
    enabled: (variables) => Boolean(variables.driverId),
  }
);

const GET_DRIVERS_DISTRICT_LIST = graphql(`
  query getDriversDistrictList($departmentId: Int!) {
    district(
      where: {
        departments_districts: { departmentId: { _eq: $departmentId } }
        isPolygone: { _eq: true }
        district_delivery_providers: {}
      }
      order_by: { name: asc }
    ) {
      id
      name
      isPolygone
      districts_coordinates {
        latitude
        longitude
      }
    }
  }
`);

const GET_WEEKDAY_WINDOWS_FOR_SLOT = graphql(`
  query GetWeekdayWindowForSlot($driverWindowId: Int!) {
    weekday_window(where: { driverWindowId: { _eq: $driverWindowId } }) {
      id
      weekday
      weekday_window_districts {
        district {
          id
          name
        }
      }
    }
  }
`);

const GET_WEEKDAY_WINDOWS_ALL = graphql(`
  query GetWeekdayWindowsAll($driverId: String!) {
    weekday_window(where: { driver_window: { driverId: { _eq: $driverId } } }) {
      id
      weekday
      weekday_window_districts {
        district {
          id
          name
        }
      }
    }
  }
`);

const GET_DRIVERS_LIST = graphql(`
  query getDriversList($departmentId: Int!) {
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: false }
        departments_users: { departmentId: { _eq: $departmentId } }
      }
    ) {
      id
      name
    }
  }
`);

const GET_DRIVER_WINDOW_SLOTS = graphql(`
  query GetDriverWindowSlots($driverId: String!) {
    driver_window(where: { driverId: { _eq: $driverId } }) {
      id
      timeDuration
      weekday_windows {
        id
        weekday
        weekend
        weekday_window_districts {
          id
          tempDateFrom
          tempDateTo
          districtId
          district {
            id
            name
            enabled
            district_delivery_providers {
              delivery_provider_id
            }
          }
        }
        window_delivery_providers {
          provider_id
          delivery_provider {
            id
            short_name
            display_name
          }
        }
      }
    }
  }
`);

const INSERT_DRIVER_WINDOW_SLOT = graphql(`
  mutation InsertDriverWindowSlot($driverId: String!, $timeDuration: String!) {
    insert_driver_window_one(object: { driverId: $driverId, timeDuration: $timeDuration }) {
      id
      timeDuration
    }
  }
`);

const INSERT_WEEKDAY_WINDOWS = graphql(`
  mutation InsertWeekdayWindow($objects: [weekday_window_insert_input!]!) {
    insert_weekday_window(objects: $objects) {
      returning {
        weekday
        id
        driver_window {
          driverId
        }
      }
      affected_rows
    }
  }
`);

const INSERT_WEEKDAY_WINDOW_DISTRICTS = graphql(`
  mutation InsertWeekdayWindowDistricts($objects: [weekday_window_district_insert_input!]!) {
    insert_weekday_window_district(
      objects: $objects
      on_conflict: {
        constraint: weekday_window_district_weekdayWindowId_districtId_key
        update_columns: [tempDateFrom, tempDateTo]
      }
    ) {
      affected_rows
      returning {
        weekday_window {
          driver_window {
            driverId
          }
        }
      }
    }
  }
`);

const REMOVE_DRIVER_WINDOW_SLOT = graphql(`
  mutation RemoveDriverWindowSlot($id: Int!) {
    delete_driver_window_by_pk(id: $id) {
      id
      driverId
    }
  }
`);

const REMOVE_WEEKDAY_WINDOW_DISTRICTS = graphql(`
  mutation RemoveWeekdayWindowDistricts($districtId: String!, $weekdayWindowIds: [Int!]!) {
    delete_weekday_window_district(
      where: { districtId: { _eq: $districtId }, weekdayWindowId: { _in: $weekdayWindowIds } }
    ) {
      affected_rows
      returning {
        weekday_window {
          driver_window {
            driverId
          }
        }
      }
    }
  }
`);

const REMOVE_WEEKDAY_WINDOW_DISTRICTS_LIST = graphql(`
  mutation RemoveWeekdayWindowDistrictsList($districtIds: [String!]!, $weekdayWindowId: Int!) {
    delete_weekday_window_district(
      where: { districtId: { _in: $districtIds }, weekdayWindowId: { _eq: $weekdayWindowId } }
    ) {
      affected_rows
      returning {
        weekday_window {
          driver_window {
            driverId
          }
        }
      }
    }
  }
`);

const REMOVE_WEEKDAY_WINDOW_DISTRICTS_BY_WINDOW_ID = graphql(`
  mutation RemoveWeekdayWindowDistrictsByWindowId($driverWindowId: Int!) {
    delete_weekday_window_district(
      where: { weekday_window: { driverWindowId: { _eq: $driverWindowId } } }
    ) {
      affected_rows
      returning {
        weekday_window {
          driver_window {
            driverId
          }
        }
      }
    }
  }
`);

const REMOVE_WEEKDAY_WINDOWS = graphql(`
  mutation RemoveWeekdayWindows($driverWindowId: Int!) {
    delete_weekday_window(where: { driverWindowId: { _eq: $driverWindowId } }) {
      affected_rows
      returning {
        driver_window {
          driverId
        }
      }
    }
  }
`);

const UPDATE_DRIVER_WINDOW_SLOT = graphql(`
  mutation UpdateDriverWindowSlot($id: Int!, $timeDuration: String!) {
    update_driver_window_by_pk(pk_columns: { id: $id }, _set: { timeDuration: $timeDuration }) {
      id
      timeDuration
      driverId
    }
  }
`);

const GET_COUNTRY_HOLIDAYS_BY_DEPARTMENT_ID = graphql(`
  query GetCountryHolidaysByDepartmentId($departmentId: Int!) {
    country_holiday(where: { country: { departments: { id: { _eq: $departmentId } } } }) {
      date
      id
    }
  }
`);

export const useGetDriverDistricts = createQueryHook(
  GET_DRIVERS_DISTRICT_LIST,
  (variables) => [QueryKeys.GET_DRIVERS_DISTRICT_LIST, variables.departmentId],
  {
    enabled: (variables) => Boolean(variables.departmentId),
  }
);

export const useGetCountryHolidaysByDepartmentId = createQueryHook(
  GET_COUNTRY_HOLIDAYS_BY_DEPARTMENT_ID,
  (variables) => [QueryKeys.GET_COUNTRY_HOLIDAYS_BY_DEPARTMENT_ID, variables.departmentId]
);

export const useGetDrivers = createQueryHook(GET_DRIVERS_LIST, (variables) => [
  QueryKeys.GET_DRIVERS_LIST,
  variables.departmentId,
]);

export const useGetDriverWindowSlots = createQueryHook(
  GET_DRIVER_WINDOW_SLOTS,
  (variables) => [QueryKeys.GET_DRIVER_WINDOW_SLOTS, variables.driverId],
  {
    enabled: (variables) => Boolean(variables.driverId),
    select: (data) => {
      if (!data?.driver_window) return data;

      const processedWindows = data.driver_window
        .filter((window) => window?.timeDuration)
        .map((window) => ({
          ...window,
          timeDuration: (window.timeDuration ?? '').replace(/"/g, ''),
        }))
        .slice()
        .sort((a, b) => {
          const [startA] = a.timeDuration.split('-').map((t: string) => t.split(':').map(Number));
          const [startB] = b.timeDuration.split('-').map((t: string) => t.split(':').map(Number));
          return startA[0] - startB[0] || startA[1] - startB[1];
        });

      return {
        ...data,
        driver_window: processedWindows,
      };
    },
  }
);

export const useGetWeekdayWindowsAll = createQueryHook(
  GET_WEEKDAY_WINDOWS_ALL,
  (variables) => [QueryKeys.GET_WEEKDAY_WINDOWS_ALL, variables.driverId],
  {
    enabled: (variables) => Boolean(variables.driverId),
  }
);

export const useGetWeekdayWindowsForSlot = createQueryHook(
  GET_WEEKDAY_WINDOWS_FOR_SLOT,
  (variables) => [QueryKeys.GET_WEEKDAY_WINDOWS_FOR_SLOT, variables.driverWindowId],
  {
    enabled: (variables) => Boolean(variables.driverWindowId),
  }
);

export const useInsertDriverWindowSlot = createMutationHook(INSERT_DRIVER_WINDOW_SLOT, {
  invalidateQueries: [
    {
      queryKey: (variables) => [QueryKeys.GET_DRIVER_WINDOW_SLOTS, variables.driverId],
    },
  ],
  errorMessage: 'Error adding driver window slot',
});

export const useInsertWeekdayWindows = createMutationHook(INSERT_WEEKDAY_WINDOWS, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
    {
      queryKey: () => [QueryKeys.GET_WEEKDAY_WINDOWS_ALL],
    },
    {
      queryKey: () => [QueryKeys.GET_WEEKDAY_WINDOWS_FOR_SLOT],
    },
  ],
  errorMessage: 'Error adding weekday windows',
});

export const useInsertWeekdayWindowDistricts = createMutationHook(INSERT_WEEKDAY_WINDOW_DISTRICTS, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
  ],
  errorMessage: 'Error adding weekday window districts',
});

export const useRemoveDriverWindowSlot = createMutationHook(REMOVE_DRIVER_WINDOW_SLOT, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
  ],
  errorMessage: 'Error removing driver window slot',
});

export const useRemoveWeekdayWindowDistricts = createMutationHook(REMOVE_WEEKDAY_WINDOW_DISTRICTS, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
  ],
  errorMessage: 'Error removing weekday window districts',
});

export const useRemoveWeekdayWindowDistrictsList = createMutationHook(
  REMOVE_WEEKDAY_WINDOW_DISTRICTS_LIST,
  {
    invalidateQueries: [
      {
        queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
      },
    ],
    errorMessage: 'Error removing weekday window districts list',
  }
);

export const useRemoveWeekdayWindowDistrictsByWindowId = createMutationHook(
  REMOVE_WEEKDAY_WINDOW_DISTRICTS_BY_WINDOW_ID,
  {
    invalidateQueries: [
      {
        queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
      },
    ],
    errorMessage: 'Error removing weekday window districts by window ID',
  }
);

export const useRemoveWeekdayWindows = createMutationHook(REMOVE_WEEKDAY_WINDOWS, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
  ],
  errorMessage: 'Error removing weekday windows',
});

export const useUpdateDriverWindowSlot = createMutationHook(UPDATE_DRIVER_WINDOW_SLOT, {
  invalidateQueries: [
    {
      queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS],
    },
  ],
  errorMessage: 'Error updating driver window slot',
});

const INSERT_DRIVER_WINDOWS = graphql(`
  mutation InsertDriverWindows($objects: [driver_window_insert_input!]!) {
    insert_driver_window(objects: $objects) {
      affected_rows
    }
  }
`);

const defaultPeruSlots = [
  '08:30-10:00',
  '09:00-11:00',
  '10:00-12:00',
  '11:00-13:00',
  '12:00-14:00',
  '13:00-15:00',
  '14:00-16:00',
  '15:00-17:00',
  '16:00-18:00',
];

export const useInsertDefaultDriverWindowSlots = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ driverId }: { driverId: string }) => {
      const existingData = await client.request(GET_DRIVER_WINDOW_SLOTS, { driverId });
      const existingSlots = new Set(existingData?.driver_window.map((dw) => dw.timeDuration));

      const newSlots = defaultPeruSlots
        .filter((slot) => !existingSlots.has(slot))
        .map((timeDuration) => ({
          driverId,
          timeDuration,
          weekday_windows: {
            data: [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({ weekday })),
          },
        }));

      return client.request(INSERT_DRIVER_WINDOWS, { objects: newSlots });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_DRIVER_WINDOW_SLOTS] });
    },
  });
};

const INSERT_WINDOW_DELIVERY_PROVIDERS = graphql(`
  mutation InsertWindowDeliveryProviders($objects: [window_delivery_providers_insert_input!]!) {
    insert_window_delivery_providers(
      objects: $objects
      on_conflict: { constraint: window_delivery_providers_pkey, update_columns: [] }
    ) {
      affected_rows
    }
  }
`);

const DELETE_WINDOW_DELIVERY_PROVIDERS = graphql(`
  mutation DeleteWindowDeliveryProviders($windowId: Int!) {
    delete_window_delivery_providers(where: { window_id: { _eq: $windowId } }) {
      affected_rows
    }
  }
`);

const DELETE_WINDOW_DELIVERY_PROVIDERS_BY_DRIVER_WINDOW = graphql(`
  mutation DeleteWindowDeliveryProvidersByDriverWindow($driverWindowId: Int!) {
    delete_window_delivery_providers(
      where: { weekday_window: { driverWindowId: { _eq: $driverWindowId } } }
    ) {
      affected_rows
    }
  }
`);

const DELETE_WINDOW_DELIVERY_PROVIDERS_BATCH = graphql(`
  mutation DeleteWindowDeliveryProvidersBatch($windowIds: [Int!]!) {
    delete_window_delivery_providers(where: { window_id: { _in: $windowIds } }) {
      affected_rows
    }
  }
`);

export const useInsertWindowDeliveryProviders = createMutationHook(
  INSERT_WINDOW_DELIVERY_PROVIDERS,
  {
    invalidateQueries: [{ queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS] }],
    errorMessage: 'Error adding window delivery providers',
  }
);

export const useDeleteWindowDeliveryProviders = createMutationHook(
  DELETE_WINDOW_DELIVERY_PROVIDERS,
  {
    invalidateQueries: [{ queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS] }],
    errorMessage: 'Error removing window delivery providers',
  }
);

export const useDeleteWindowDeliveryProvidersByDriverWindow = createMutationHook(
  DELETE_WINDOW_DELIVERY_PROVIDERS_BY_DRIVER_WINDOW,
  {
    errorMessage: 'Error removing window delivery providers by driver window',
  }
);

export const useDeleteWindowDeliveryProvidersBatch = createMutationHook(
  DELETE_WINDOW_DELIVERY_PROVIDERS_BATCH,
  {
    invalidateQueries: [{ queryKey: () => [QueryKeys.GET_DRIVER_WINDOW_SLOTS] }],
    errorMessage: 'Error removing window delivery providers',
  }
);

export const useUpdateWeekdayWindowProviders = () => {
  const { mutateAsync: deleteProviders } = useDeleteWindowDeliveryProviders();
  const { mutateAsync: insertProviders } = useInsertWindowDeliveryProviders();

  return useMutation({
    mutationFn: async ({
      weekdayWindowId,
      providerIds,
    }: {
      weekdayWindowId: number;
      providerIds: string[];
    }) => {
      await deleteProviders({ windowId: weekdayWindowId });

      if (providerIds.length > 0) {
        await insertProviders({
          objects: providerIds.map((providerId) => ({
            window_id: weekdayWindowId,
            provider_id: providerId,
          })),
        });
      }

      return { success: true, weekdayWindowId, providerIds };
    },
  });
};

export const useUpdateDriverWindowProviders = () => {
  const { mutateAsync: deleteProvidersBatch } = useDeleteWindowDeliveryProvidersBatch();
  const { mutateAsync: insertProviders } = useInsertWindowDeliveryProviders();

  return useMutation({
    mutationFn: async ({
      weekdayWindowIds,
      providerIds,
    }: {
      weekdayWindowIds: number[];
      providerIds: string[];
    }) => {
      await deleteProvidersBatch({ windowIds: weekdayWindowIds });

      if (providerIds.length > 0) {
        const objects = weekdayWindowIds.flatMap((windowId) =>
          providerIds.map((providerId) => ({
            window_id: windowId,
            provider_id: providerId,
          }))
        );
        await insertProviders({ objects });
      }

      return { success: true, weekdayWindowIds, providerIds };
    },
  });
};
